/**
 * Página de Política de Privacidad - Estilo Awwwards
 *
 * Contiene el texto íntegro y verbatim de la Política de Privacidad
 * facilitada por el cliente (documento Word convertido a texto plano).
 * Solo se corrigen artefactos de conversión evidentes (tabulaciones
 * sueltas, espacios dobles); el contenido legal, su redacción y
 * cualquier errata original ("repersentación", etc.) se mantienen tal
 * cual, porque es texto legal y no debe reinterpretarse.
 *
 * El colectivo "RECETAS" puede enlazarse directamente mediante
 * `/privacidad#recetas`: en ese caso su grupo del acordeón se muestra
 * abierto por defecto y la página hace scroll automático hasta él.
 * El resto de colectivos permanecen cerrados por defecto.
 */

import React, { useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../components/animations';
import PrivacyAccordionItem from '../components/legal/PrivacyAccordionItem';

gsap.registerPlugin(ScrollTrigger);

/**
 * Datos de los colectivos desplegables de la Política de Privacidad.
 *
 * Se modela como datos (array de secciones) separados del renderizado
 * (principio de responsabilidad única): cada colectivo tiene un `id`
 * (usado como ancla y para los atributos ARIA del acordeón), un
 * `title` y una lista de `blocks` que describen, en orden, las
 * preguntas, párrafos y listados del documento original.
 *
 * Tipos de bloque soportados por `renderPolicyBlocks`:
 *  - { type: 'question', text }  -> subtítulo en negrita
 *  - { type: 'paragraph', text } -> párrafo de texto
 *  - { type: 'list', items }     -> lista con viñetas
 */
const privacyGroups = [
  {
    id: 'contactos-web',
    title: 'CONTACTOS DE LA WEB O DEL CORREO ELECTRÓNICO',
    blocks: [
      { type: 'question', text: '¿Qué datos recopilamos a través de la Web?' },
      { type: 'paragraph', text: 'Podemos tratar tu IP, qué sistema operativo o navegador usas, e incluso la duración de tu visita, de forma anónima.' },
      { type: 'paragraph', text: 'Si nos facilitas datos en el formulario de contacto, te identificarás para poder contactar contigo, en caso de que sea necesario.' },
      { type: 'question', text: '¿Con que finalidades vamos a tratar sus datos personales?' },
      { type: 'list', items: [
        'Contestar a tus consultas, solicitudes o peticiones.',
        'Gestionar el servicio solicitado, contestar tu solicitud, o tramitar tu petición.',
        'Información por medios electrónicos, que versen sobre tu solicitud.',
        'Información comercial o de eventos por medios electrónicos, siempre que exista autorización expresa.',
        'Realizar análisis y mejoras en la Web, sobre nuestros productos y servicios. Mejorar nuestra estrategia comercial.',
      ] },
      { type: 'question', text: '¿Cuál es la legitimación para el tratamiento de tus datos?' },
      { type: 'paragraph', text: 'La aceptación y consentimiento del interesado, otorgado a través de una acción voluntaria, como en los casos donde para realizar una solicitud sea necesario cumplimentar un formulario y hacer un "click" en el botón de enviar, la realización del mismo implicará necesariamente que ha sido informado y ha otorgado expresamente su consentimiento al contenido de la cláusula anexada a dicho formulario o aceptación de la política de privacidad.' },
      { type: 'paragraph', text: 'Todos nuestros formularios cuentan con el símbolo * en los datos obligatorios. Si no facilitas esos campos, o no marcas el checkbox de aceptación de la política de privacidad, no se permitirá el envío de la información.' },
      { type: 'question', text: '¿Durante cuánto tiempo vamos a mantener los datos personales?' },
      { type: 'paragraph', text: 'Hasta la revocación del consentimiento otorgado.' },
    ],
  },
  {
    id: 'clientes',
    title: 'CLIENTES',
    blocks: [
      { type: 'question', text: '¿Con que finalidades vamos a tratar sus datos personales?' },
      { type: 'list', items: [
        'Elaboración del presupuesto y seguimiento del mismo mediante comunicaciones entre ambas partes.',
        'Información por medios electrónicos, que versen sobre tu solicitud.',
        'Información comercial o de eventos por medios electrónicos, siempre que exista autorización expresa.',
        'Gestionar los servicios administrativos, de comunicaciones y de logística realizados por el Responsable.',
        'Facturación y declaración de los impuestos oportunos.',
        'Realizar las transacciones que correspondan.',
        'Gestiones de control y recobro.',
      ] },
      { type: 'question', text: '¿Cuál es la legitimación para el tratamiento de tus datos?' },
      { type: 'paragraph', text: 'La existencia de una relación contractual entre las partes. El tratamiento es necesario para la ejecución de un contrato en el que el interesado es parte o para la aplicación a petición de este de medidas precontractuales.' },
      { type: 'question', text: '¿Durante cuánto tiempo vamos a mantener los datos personales?' },
      { type: 'paragraph', text: 'Durante la vigencia de las relaciones entre las partes y por los plazos de prescripción derivados de las mismas.' },
    ],
  },
  {
    id: 'recetas',
    title: 'RECETAS',
    blocks: [
      { type: 'question', text: '¿Con que finalidades vamos a tratar sus datos personales?' },
      { type: 'paragraph', text: 'Trataremos su nombre, apellidos y dirección de correo electrónico con la finalidad de gestionar la recepción, revisión y publicación en nuestra página web de las recetas que nos comparta, así como para contactar con usted en relación con dicha publicación cuando resulte necesario.' },
      { type: 'paragraph', text: 'El nombre, los apellidos y dirección de correo electrónico se utilizará únicamente para la gestión de la receta y no será publicada en la página web.' },
      { type: 'question', text: '¿Cuál es la legitimación para el tratamiento de sus datos?' },
      { type: 'paragraph', text: 'La base jurídica para el tratamiento de sus datos personales es el consentimiento del interesado.' },
      { type: 'paragraph', text: 'El consentimiento se otorgará mediante una acción afirmativa y voluntaria, marcando la casilla habilitada al efecto en el formulario correspondiente. El interesado podrá retirar su consentimiento en cualquier momento, sin que ello afecte a la licitud del tratamiento realizado con anterioridad a su retirada.' },
      { type: 'paragraph', text: 'Los campos señalados con un asterisco (*) son obligatorios. Si no se facilitan dichos datos o no se marca la casilla de aceptación correspondiente, no será posible enviar la receta a través del formulario.' },
      { type: 'question', text: '¿Durante cuánto tiempo vamos a conservar sus datos personales?' },
      { type: 'paragraph', text: 'Los datos personales se conservarán mientras la receta permanezca publicada y el interesado no retire el consentimiento otorgado.' },
    ],
  },
  {
    id: 'encuestas-calidad',
    title: 'ENCUESTAS DE CALIDAD',
    blocks: [
      { type: 'question', text: '¿Con que finalidades vamos a tratar sus datos personales?' },
      { type: 'list', items: [
        'Valorar el grado de calidad en el servicio dispensado',
        'Mejorar los servicios ofrecidos, en virtud del cumplimiento de la ISO',
      ] },
      { type: 'question', text: '¿Cuál es la legitimación para el tratamiento de tus datos?' },
      { type: 'paragraph', text: 'La base legal es el consentimiento expreso del encuestado.' },
      { type: 'question', text: '¿Durante cuánto tiempo vamos a mantener los datos personales?' },
      { type: 'paragraph', text: 'Hasta la revocación del consentimiento otorgado.' },
    ],
  },
  {
    id: 'proveedores',
    title: 'PROVEEDORES',
    blocks: [
      { type: 'question', text: '¿Con que finalidades vamos a tratar sus datos personales?' },
      { type: 'list', items: [
        'Información por medios electrónicos, que versen sobre tu solicitud.',
        'Información comercial o de eventos por medios electrónicos, siempre que exista autorización expresa.',
        'Gestionar los servicios administrativos, de comunicaciones y de logística realizados por el Responsable.',
        'Facturación.',
        'Realizar las transacciones que correspondan.',
        'Facturación y declaración de los impuestos oportunos.',
      ] },
      { type: 'question', text: '¿Cuál es la legitimación para el tratamiento de tus datos?' },
      { type: 'paragraph', text: 'La base legal es la aceptación de una relación contractual, o en su defecto tu consentimiento al contactar con nosotros u ofrecernos sus productos por alguna vía.' },
      { type: 'question', text: '¿Durante cuánto tiempo vamos a mantener los datos personales?' },
      { type: 'paragraph', text: 'Durante la vigencia de las relaciones entre las partes y por los plazos de prescripción derivados de las mismas.' },
    ],
  },
  {
    id: 'redes-sociales',
    title: 'CONTACTOS REDES SOCIALES',
    blocks: [
      { type: 'question', text: '¿Con que finalidades vamos a tratar sus datos personales?' },
      { type: 'list', items: [
        'Contestar a tus consultas, solicitudes o peticiones.',
        'Gestionar el servicio solicitado, contestar tu solicitud, o tramitar tu petición.',
        'Relacionarnos contigo y crear una comunidad de seguidores.',
      ] },
      { type: 'question', text: '¿Cuál es la legitimación para el tratamiento de tus datos?' },
      { type: 'paragraph', text: 'Las bases que legitiman el tratamiento es el consentimiento voluntario del interesado de ponerse en contacto y, en su caso, la aceptación de una relación contractual en el entorno de la red social que corresponda. El tratamiento de los datos dentro de la Red Social se realizará conforme a sus políticas de Privacidad.' },
      { type: 'question', text: '¿Durante cuánto tiempo vamos a mantener los datos personales?' },
      { type: 'paragraph', text: 'Sólo podemos consultar o dar de baja tus datos de forma restringida al tener un perfil específico. Los trataremos tanto tiempo como tu nos dejes siguiéndonos, siendo amigos o dándole a "me gusta", "seguir" o botones similares.' },
      { type: 'paragraph', text: 'Cualquier rectificación de tus datos o restricción de información o de publicaciones debes realizarla a través de la configuración de tu perfil o usuario en la propia red social.' },
    ],
  },
  {
    id: 'transparencia',
    title: 'TRANSPARENCIA',
    blocks: [
      { type: 'question', text: '¿Con que finalidades vamos a tratar sus datos personales?' },
      { type: 'paragraph', text: 'Gestión del Portal de Transparencia' },
      { type: 'question', text: '¿Cuál es la legitimación para el tratamiento de tus datos?' },
      { type: 'paragraph', text: 'Ley 12/2014, de 26 de diciembre, de transparencia y de acceso a la información pública' },
      { type: 'paragraph', text: 'Ley 19/2013, de 9 de diciembre, de transparencia, acceso a la información pública y buen gobierno' },
    ],
  },
  {
    id: 'informacion-adicional',
    title: 'INFORMACIÓN ADICIONAL SOBRE LOS TRATAMIENTOS DE DATOS PERSONALES',
    blocks: [
      { type: 'question', text: '¿Incluimos datos personales de terceras personas?' },
      { type: 'paragraph', text: 'No, como norma general sólo tratamos los datos que nos facilitan los titulares. Si nos aportas datos de terceros, deberás con carácter previo, informar y solicitar su consentimiento a dichas personas, o de lo contrario nos eximes de cualquier responsabilidad por el incumplimiento de éste requisito.' },
      { type: 'question', text: '¿Y datos de menores?' },
      { type: 'paragraph', text: 'No tratamos datos de menores de 14 años. Por tanto, absténgase de facilitarlos si no tiene esa edad o, en su caso, de facilitar datos de terceros que no tengan la citada edad. INTERCASA, S.A. se exime de cualquier responsabilidad por el incumplimiento de esta previsión.' },
      { type: 'question', text: '¿Realizaremos comunicaciones por medios electrónicos?' },
      { type: 'list', items: [
        'Sólo se realizarán para gestionar tu solicitud, si es uno de los medios de contacto que nos ha facilitado.',
        'Si realizamos comunicaciones comerciales habrán sido previa y expresamente autorizadas por tí.',
      ] },
      { type: 'question', text: '¿Qué medidas de seguridad aplicamos?' },
      { type: 'paragraph', text: 'Puedes estar tranquilo: Hemos adoptado un nivel óptimo de protección de los Datos Personales que manejamos, y hemos instalado todos los medios y medidas técnicas a nuestra disposición según el estado de la tecnología para evitar la pérdida, mal uso, alteración, acceso no autorizado y robo de los Datos Personales.' },
      { type: 'question', text: '¿A qué destinatarios se comunicarán tus datos?' },
      { type: 'paragraph', text: 'Tus datos no se cederán a terceros, salvo obligación legal. En concreto se comunicarán a la Agencia Estatal de la Administración Tributaria y a bancos y entidades financieras para el cobro del servicio prestado o producto adquirido como a los encargados del tratamiento necesarios para la ejecución del acuerdo.' },
      { type: 'paragraph', text: 'En caso de compra o pago, si eliges alguna aplicación, web, plataforma, tarjeta bancaria, o algún otro servicio online, tus datos se cederán a esa plataforma o se tratarán en su entorno, siempre con la máxima seguridad.' },
      { type: 'paragraph', text: 'Cuando se lo ordenemos, tendrán acceso a nuestra web la empresa de desarrollo y mantenimiento web, alojamiento web/hosting. Las mismas tendrán firmado un contrato de prestación de servicios que les obliga a mantener el mismo nivel de privacidad que nosotros.' },
      { type: 'question', text: '¿Qué Derechos tienes?' },
      { type: 'list', items: [
        'A saber si estamos tratando tus datos o no.',
        'A acceder a tus datos personales.',
        'A solicitar la rectificación de tus datos si son inexactos.',
        'A solicitar la supresión de tus datos si ya no son necesarios para los fines para los que fueron recogidos o si nos retiras el consentimiento otorgado.',
        'A solicitar la limitación del tratamiento de sus datos, en algunos supuestos, en cuyo caso sólo los conservaremos de acuerdo con la normativa vigente.',
        'A portar tus datos, que te serán facilitados en un formato estructurado, de uso común o lectura mecánica. Si lo prefieres, se los podemos enviar al nuevo responsable que nos designes. Sólo es válido en determinados supuestos.',
        'A presentar una reclamación ante la Agencia Española de Protección de Datos o autoridad de control competente, si crees que no te hemos atendido correctamente.',
        'A revocar el consentimiento para cualquier tratamiento para el que hayas consentido, en cualquier momento.',
      ] },
      { type: 'paragraph', text: 'Si modificas algún dato, te agradecemos que nos lo comuniques para mantenerlos actualizados.' },
      { type: 'question', text: '¿Quieres un formulario para el ejercicio de Derechos?' },
      { type: 'list', items: [
        'Tenemos formularios para el ejercicio de tus derechos, pídenoslos por email o si lo prefieres, puedes usar los elaborados por la Agencia Española de Protección de Datos o terceros.',
        'Estos formularios deben ir firmados',
        'Si te representa alguien, debes acreditar la repersentación.',
        'Los formularios pueden ser presentados presencialmente, enviados por carta o por mail en la dirección del Responsable al inicio de este texto.',
      ] },
      { type: 'question', text: '¿Cuánto tardamos en contestarte al Ejercicio de Derechos?' },
      { type: 'paragraph', text: 'Depende del derecho, pero como máximo en un mes desde tu solicitud, y dos meses si el tema es muy complejo y te notificamos que necesitamos más tiempo.' },
      { type: 'question', text: '¿Tratamos cookies?' },
      { type: 'paragraph', text: 'Si usamos otro tipo de cookies que no sean las necesarias, podrás consultar la política de cookies en el enlace correspondiente desde el inicio de nuestra web.' },
      { type: 'question', text: '¿Durante cuánto tiempo vamos a mantener tus datos personales?' },
      { type: 'list', items: [
        'Los datos personales serán mantenidos mientras sigas vinculado con nosotros.',
        'Una vez te desvincules, los datos personales tratados en cada finalidad se mantendrán durante los plazos legalmente previstos, incluido el plazo en el que un juez o tribunal los pueda requerir atendiendo al plazo de prescripción de acciones judiciales.',
        'Los datos tratados se mantendrán en tanto no expiren los plazos legales aludidos anteriormente, si hubiera obligación legal de mantenimiento, o de no existir ese plazo legal, hasta que el interesado solicite su supresión o revoque el consentimiento otorgado.',
        'Mantendremos toda la información y comunicaciones relativas a tu compra o a la prestación de nuestro servicio, mientras duren las garantías de los productos o servicios, para atender posibles reclamaciones.',
      ] },
    ],
  },
];

/**
 * Renderiza la lista de bloques (preguntas, párrafos y listados) de un
 * colectivo de la Política de Privacidad, manteniendo la separación
 * entre los datos (`privacyGroups`) y su presentación.
 *
 * @param {Array<Object>} blocks - Bloques de contenido del colectivo
 * @returns {JSX.Element} Fragmento con el contenido renderizado
 */
const renderPolicyBlocks = (blocks) => (
  <div className="space-y-4">
    {blocks.map((block, index) => {
      // eslint-disable-next-line react/no-array-index-key
      const key = `${block.type}-${index}`;
      if (block.type === 'question') {
        return (
          <h4 key={key} className="font-baskerville text-base md:text-lg font-bold text-intercasa-dark pt-2">
            {block.text}
          </h4>
        );
      }
      if (block.type === 'list') {
        return (
          <ul key={key} className="space-y-2 text-intercasa-gray text-base leading-relaxed">
            {block.items.map((item) => (
              <li key={item} className="flex items-start">
                <span className="text-intercasa-red mr-3 mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      }
      return (
        <p key={key} className="text-intercasa-gray text-base leading-relaxed">
          {block.text}
        </p>
      );
    })}
  </div>
);

/**
 * Página de Política de Privacidad con estilo Awwwards
 * @returns {JSX.Element} Página de política de privacidad inmersiva
 */
const PrivacyPage = () => {
  const heroRef = useRef(null);
  const location = useLocation();
  // El colectivo RECETAS solo se abre por defecto cuando se accede con el
  // ancla `#recetas` (p.ej. desde el formulario de envío de recetas).
  const recetasOpenByHash = location.hash === '#recetas';

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return undefined;

    const tl = gsap.timeline();
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );

    return () => tl.kill();
  }, []);

  // Si se llega con el ancla #recetas, hace scroll hasta el bloque una vez
  // que el acordeón se ha expandido y el `ScrollToTop` global (que fuerza
  // scroll(0,0) al montar la ruta) ha terminado sus reintentos.
  useEffect(() => {
    if (location.hash !== '#recetas') return undefined;

    const timer = setTimeout(() => {
      const target = document.getElementById('recetas');
      if (!target) return;

      if (window.__lenis) {
        window.__lenis.scrollTo(target, { offset: -120 });
      } else {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [location.hash]);

  return (
    <ImmersiveLayout>
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-[60vh] flex items-end pb-24 md:pb-32 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />

        {/* Imagen en patrón repetido (tile) */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />

        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />

        {/* Contenido del hero */}
        <div className="relative z-10 container mx-auto px-4 md:px-8">
          <div className="max-w-4xl">
            <h1 className="hero-title font-baskerville font-bold text-white text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] mb-8 drop-shadow-lg">
              Política de <span className="text-intercasa-yellow">Privacidad</span>
            </h1>
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              Protección y tratamiento de datos personales
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Introducción */}
            <AnimatedSection animation="fadeUp">
              <div className="mb-16">
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  La Política de Privacidad forma parte de las Condiciones Generales que rigen la presente Web.
                </p>
              </div>
            </AnimatedSection>

            {/* Responsable del tratamiento */}
            <AnimatedSection animation="fadeUp" delay={0.1}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  ¿Quién es el responsable del tratamiento de tus datos?
                </h2>
                <div className="bg-intercasa-bone p-8 rounded-2xl border-l-4 border-intercasa-red space-y-2 text-intercasa-gray text-lg">
                  <p>INTERCASA, S.A.</p>
                  <p>Domicilio: Barranco de Juncalillo, km 42.5, 35107 San Bartolomé de Tirajana, Gran Canaria. Las Palmas</p>
                  <p>CIF: A-35002559</p>
                  <p>
                    Tfno:{' '}
                    <a href="tel:+34928732001" className="text-intercasa-red underline hover:no-underline">
                      +34 928 732 001
                    </a>
                  </p>
                  <p>
                    Correo electronico:{' '}
                    <a href="mailto:info@intercasacanarias.es" className="text-intercasa-red underline hover:no-underline">
                      info@intercasacanarias.es
                    </a>
                  </p>
                  <p>
                    Delegado de protección de datos:{' '}
                    <a href="mailto:dpo@livvohotels.com" className="text-intercasa-red underline hover:no-underline">
                      dpo@livvohotels.com
                    </a>
                  </p>
                </div>
                <p className="text-intercasa-gray text-lg leading-relaxed mt-6">
                  Puedes dirigirte de cualquier forma para comunicarte con nosotros.
                </p>
                <p className="text-intercasa-gray text-lg leading-relaxed mt-4">
                  Nos reservamos el derecho a modificar o adaptar la presente Política de Privacidad en cualquier momento. Te recomendamos revisar la misma, y si te has registrado y accedes a tu cuenta o perfil, se te informará de las modificaciones.
                </p>
              </div>
            </AnimatedSection>

            {/* Colectivos: acordeón */}
            <AnimatedSection animation="fadeUp" delay={0.2}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  Colectivos
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed mb-8">
                  Si eres alguno de los siguientes colectivos, consulta la información desplegable:
                </p>
                <div className="space-y-4">
                  {privacyGroups.map((group) => {
                    const isRecetas = group.id === 'recetas';
                    return (
                      <PrivacyAccordionItem
                        // El grupo de RECETAS se remonta cuando cambia el ancla de
                        // la URL, de forma que recoja su nuevo `defaultOpen`.
                        key={isRecetas ? `recetas-${recetasOpenByHash}` : group.id}
                        id={group.id}
                        title={group.title}
                        defaultOpen={isRecetas && recetasOpenByHash}
                      >
                        {renderPolicyBlocks(group.blocks)}
                      </PrivacyAccordionItem>
                    );
                  })}
                </div>
              </div>
            </AnimatedSection>

            {/* Contacto */}
            <AnimatedSection animation="fadeUp" delay={0.3}>
              <div>
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  Contacto
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed mb-8">
                  Para ejercer sus derechos o para cualquier consulta relacionada con la privacidad, puede contactarnos a través de nuestro formulario de contacto.
                </p>
                <Link
                  to="/contacto"
                  className="inline-flex items-center gap-2 bg-intercasa-red hover:bg-intercasa-red/90 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                >
                  Ir a Contacto
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default PrivacyPage;
